/**
 * `<ChangeStateMenu/>` — change a node's state to any of the four (B2.8).
 *
 * Issue #760 (Phase 2). A "Set state" list of the four factual states
 * (`state.ts`, reused — one vocab everywhere) with a ✓ on the current state.
 * Picking a state PATCHes it (→ one `state_changed` event). Picking
 * "Blocked" surfaces an optional-reason step before committing, matching the
 * gallery — the reason itself is a **note** (`task_notes`, §A6).
 *
 * Issue #767 wired the seam: when an `onSaveReason` callback is supplied (the
 * notes layer is on) and the user types a reason then hits "save", the Blocked
 * state is committed AND the reason is persisted as a note on the node. When
 * no callback is supplied (notes off / a surface that hasn't wired it), the
 * step degrades to its original calm placeholder — "skip" and "save" both
 * commit Blocked and the reason is not persisted. Factual copy, never a tally.
 *
 * §A6: state is factual ("Blocked", not judgmental). §A9: Blocked renders in
 * calm amber from `state.ts`, never alarm red.
 *
 * Editorial revamp (#1043, Wave 1 · B2.8): theme-aware. The style forks on the
 * per-team `task_editorial_ui` flag via `useMapTheme()`:
 *   - LEGACY (flag off): byte-identical to today — the chip-tinted state rows,
 *     teal "save" button, `#F7F8FA` reason field.
 *   - EDITORIAL (flag on): the wireframe `V_B28` — a `--surface` card on a
 *     `--rule` hairline, a mono `SET STATE` header, rows with a `<StatusDot/>`
 *     + label (selected row tinted `--bg-2` with a sage ✓), and a separate
 *     blocked-reason card whose header band is the calm `--st-block-bg`
 *     (#F5E6CC) AMBER — never red — with an editorial textarea + `.t-btn`
 *     skip/save buttons.
 * The four states, the state-change + reason-capture behaviour, copy, and every
 * `data-testid` are unchanged.
 */

import { useState } from "react";

import { useMapTheme } from "../theme/MapThemeContext";
import { PALETTE } from "../tree/palette";
import { TASK_STATES, type TaskState } from "../tree/state";
import { StatusDot } from "./StatusChip";

export interface ChangeStateMenuProps {
  /** The node's current state (gets the ✓). */
  current: TaskState;
  /** Commit a new state (→ PATCH → one `state_changed` event). */
  onSetState: (state: TaskState) => void;
  /** Dismiss the menu. */
  onClose: () => void;
  /** Layout: anchored dropdown (under a control) or a free panel. */
  variant?: "anchored" | "panel";
  /**
   * Persist a Blocked reason as a note on the node (#767). Supplied only when
   * the `task_notes` layer is on; when present, a non-empty reason typed before
   * "save" is written as a note. When absent the reason step is a calm
   * placeholder (the reason isn't persisted) — unchanged from #760.
   */
  onSaveReason?: (reason: string) => void;
}

export function ChangeStateMenu({
  current,
  onSetState,
  onClose,
  variant = "anchored",
  onSaveReason,
}: ChangeStateMenuProps) {
  const theme = useMapTheme();
  const c = theme.palette;
  const editorial = theme.mode === "editorial";

  // When the user picks "Blocked" we step into the optional-reason prompt
  // before committing (the gallery's two-panel B2.8). Any other state commits
  // immediately.
  const [reasonStep, setReasonStep] = useState(false);
  const [reason, setReason] = useState("");

  const commit = (state: TaskState) => {
    onSetState(state);
    onClose();
  };

  // "save" on the reason step: commit Blocked, and — when the notes layer is
  // wired (#767) — persist a non-empty reason as a note. "skip" just commits.
  const saveBlocked = () => {
    const trimmed = reason.trim();
    if (onSaveReason && trimmed) onSaveReason(trimmed);
    commit("blocked");
  };

  const pick = (state: TaskState) => {
    if (state === "blocked") {
      setReasonStep(true);
      return;
    }
    commit(state);
  };

  // Editorial: an `overflow-hidden` surface card on a `--rule` hairline (so the
  // amber reason-header band can reach the card edge) with the wireframe's soft
  // shadow. Legacy: byte-identical padded white card.
  const wrapperClass = editorial
    ? variant === "panel"
      ? "w-[256px] overflow-hidden rounded-lg border"
      : "absolute z-30 mt-1 w-[240px] overflow-hidden rounded-lg border"
    : variant === "panel"
      ? "w-[256px] rounded-lg border bg-white p-3 shadow-xl"
      : "absolute z-30 mt-1 w-[240px] rounded-lg border bg-white p-3 shadow-xl";

  if (reasonStep) {
    // The calm amber state colours (`--st-block-*`) carry the reason header.
    const block = theme.stateStyle("blocked");
    if (editorial) {
      return (
        <div
          data-testid="change-state-reason"
          className={wrapperClass}
          style={{
            borderColor: c.rule,
            backgroundColor: c.cardBg,
            borderRadius: theme.radii.lg,
            boxShadow: "0 12px 36px rgba(0,0,0,0.10)",
            fontFamily: theme.fonts.sans,
          }}
          aria-label="Blocked — add a reason"
        >
          {/* Amber header band — calm `--st-block-bg` (#F5E6CC), never red. */}
          <div
            className="border-b px-4 py-3"
            style={{ borderColor: c.rule, backgroundColor: block.chipBg }}
          >
            <span
              className="text-[10px] font-medium uppercase"
              style={{ fontFamily: theme.fonts.mono, letterSpacing: "0.1em", color: block.fg }}
            >
              Blocked — add a reason (optional)
            </span>
          </div>
          <div className="p-4">
            <textarea
              data-testid="blocked-reason-input"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Waiting on ethics board reply…"
              aria-label="Blocked reason"
              rows={3}
              className="w-full resize-none rounded-md border p-2.5 text-[13px] outline-none focus:border-current"
              style={{ borderColor: c.rule2, backgroundColor: c.cardBg, color: c.ink }}
            />
            {/* When the notes layer is on (#767) the reason is saved as a note;
                otherwise the field is a calm placeholder (not persisted). */}
            <div className="mt-1 text-[10px]" style={{ color: c.faint }}>
              {onSaveReason
                ? "Saved as a note on this task."
                : "Reason notes arrive in a later update."}
            </div>
            <div className="mt-3.5 flex items-center justify-between gap-2">
              <button
                type="button"
                data-testid="blocked-reason-skip"
                onClick={() => commit("blocked")}
                className="rounded-md border px-3 py-1 text-[11.5px] transition-colors hover:opacity-80"
                style={{ borderColor: c.rule2, color: c.ink, backgroundColor: "transparent" }}
              >
                skip
              </button>
              <button
                type="button"
                data-testid="blocked-reason-save"
                onClick={saveBlocked}
                className="rounded-md border px-3 py-1 text-[11.5px] font-medium transition-opacity hover:opacity-90"
                style={{ borderColor: c.ink, backgroundColor: c.ink, color: c.canvas }}
              >
                save
              </button>
            </div>
          </div>
        </div>
      );
    }
    return (
      <div
        data-testid="change-state-reason"
        className={wrapperClass}
        style={{ borderColor: PALETTE.cardBorder }}
        aria-label="Blocked — add a reason"
      >
        <div className="text-sm font-semibold" style={{ color: PALETTE.ink }}>
          Blocked — add a reason (optional)
        </div>
        <textarea
          data-testid="blocked-reason-input"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Waiting on ethics board reply…"
          aria-label="Blocked reason"
          rows={2}
          className="mt-2 w-full resize-none rounded-lg border p-2 text-xs outline-none"
          style={{
            borderColor: PALETTE.cardBorder,
            backgroundColor: "#F7F8FA",
            color: PALETTE.ink,
          }}
        />
        {/* When the notes layer is on (#767) the reason is saved as a note;
            otherwise the field is a calm placeholder (not persisted). */}
        <div className="mt-1 text-[10px]" style={{ color: PALETTE.faint }}>
          {onSaveReason
            ? "Saved as a note on this task."
            : "Reason notes arrive in a later update."}
        </div>
        <div className="mt-2 flex items-center justify-end gap-2">
          <button
            type="button"
            data-testid="blocked-reason-skip"
            onClick={() => commit("blocked")}
            className="rounded-md border px-3 py-1 text-[11px] transition-colors hover:bg-slate-50"
            style={{ borderColor: PALETTE.softBorder, color: PALETTE.muted }}
          >
            skip
          </button>
          <button
            type="button"
            data-testid="blocked-reason-save"
            onClick={saveBlocked}
            className="rounded-md px-3 py-1 text-[11px] font-semibold text-white transition-opacity hover:opacity-90"
            style={{ backgroundColor: PALETTE.teal }}
          >
            save
          </button>
        </div>
      </div>
    );
  }

  if (editorial) {
    const done = theme.stateStyle("done");
    return (
      <div
        data-testid="change-state-menu"
        className={wrapperClass}
        style={{
          borderColor: c.rule,
          backgroundColor: c.cardBg,
          borderRadius: theme.radii.lg,
          boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
          fontFamily: theme.fonts.sans,
        }}
        role="menu"
        aria-label="Set state"
      >
        <div className="border-b px-3.5 py-2.5" style={{ borderColor: c.rule }}>
          <span
            className="text-[10px] font-medium uppercase"
            style={{ fontFamily: theme.fonts.mono, letterSpacing: "0.1em", color: c.muted }}
          >
            Set state
          </span>
        </div>
        {TASK_STATES.map((s) => {
          const isCurrent = s.state === current;
          return (
            <button
              key={s.state}
              type="button"
              role="menuitemradio"
              aria-checked={isCurrent}
              data-testid={`change-state-${s.state}`}
              onClick={() => pick(s.state)}
              className="flex w-full items-center gap-2.5 border-b border-dashed px-3.5 py-2.5 text-left text-[13px] transition-colors last:border-b-0 hover:opacity-90"
              style={{
                borderColor: c.rule,
                backgroundColor: isCurrent ? c.bgRecess : "transparent",
                color: c.ink,
                fontWeight: isCurrent ? 600 : 400,
              }}
            >
              <StatusDot state={s.state} />
              <span>{s.label}</span>
              {s.state === "blocked" && !isCurrent ? (
                <span className="ml-auto text-[11px] italic" style={{ color: c.muted }}>
                  → add reason
                </span>
              ) : isCurrent ? (
                <span aria-hidden className="ml-auto" style={{ color: done.dot ?? done.fg }}>
                  ✓
                </span>
              ) : null}
            </button>
          );
        })}
      </div>
    );
  }

  return (
    <div
      data-testid="change-state-menu"
      className={wrapperClass}
      style={{ borderColor: PALETTE.cardBorder }}
      role="menu"
      aria-label="Set state"
    >
      <div
        className="px-1 pb-1 text-[10px] font-bold uppercase tracking-wide"
        style={{ color: PALETTE.muted }}
      >
        Set state
      </div>
      <div className="mt-1 flex flex-col gap-1">
        {TASK_STATES.map((s) => {
          const isCurrent = s.state === current;
          return (
            <button
              key={s.state}
              type="button"
              role="menuitemradio"
              aria-checked={isCurrent}
              data-testid={`change-state-${s.state}`}
              onClick={() => pick(s.state)}
              className="flex items-center justify-between gap-2 rounded-md px-2 py-2 text-left text-xs transition-colors hover:opacity-90"
              style={{
                backgroundColor: isCurrent ? s.chipBg : "transparent",
                color: s.fg,
                fontWeight: isCurrent ? 600 : 400,
              }}
            >
              <span className="flex items-center gap-2">
                <span aria-hidden>{s.glyph}</span>
                {s.label}
              </span>
              {s.state === "blocked" && !isCurrent ? (
                <span className="text-[10px]" style={{ color: s.fg }}>
                  → add reason
                </span>
              ) : isCurrent ? (
                <span aria-hidden style={{ color: s.fg }}>
                  ✓
                </span>
              ) : null}
            </button>
          );
        })}
      </div>
    </div>
  );
}
